import { unref } from 'vue'
import { useContext } from './context'
import { useMutation, useQueryClient, useQuery } from '@/composables/vue-query'
import { MaybeRef } from '@/types/types'
import { PrivacySetting, ShareToken } from '@/types/trip'

export const shareTokenQueryKeys = {
  all: () => ['trips'] as const,
  lists: () => [...shareTokenQueryKeys.all(), 'list'] as const,
  list: (tripId: MaybeRef<string>) =>
    [...shareTokenQueryKeys.lists(), tripId] as const,
  details: () => [...shareTokenQueryKeys.all(), 'details'] as const,
  detail: (tripId: MaybeRef<string>, token: MaybeRef<string>) =>
    [...shareTokenQueryKeys.all(), 'details', tripId, token] as const,
}

export function useShareTokensList(tripId: MaybeRef<string>) {
  const { $axios } = useContext()

  return useQuery({
    queryKey: shareTokenQueryKeys.list(tripId),
    queryFn: async () => {
      const { data } = await $axios.get<ShareToken[]>(
        `/api/trips/${unref(tripId)}/share-tokens`,
      )
      return data
    },
  })
}

export interface CreateShareTokenInput {
  name: string
  privacySettings: PrivacySetting[]
  isActive?: boolean
}

export function useCreateShareToken(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateShareTokenInput) => {
      const { data: createdData } = await $axios.post<ShareToken>(
        `/api/trips/${unref(tripId)}/share-tokens`,
        data,
      )
      return createdData
    },
    async onSuccess() {
      await queryClient.invalidateQueries(shareTokenQueryKeys.list(tripId))
    },
  })
}

export interface UpdateShareTokenInput {
  token: string
  name?: string
  privacySettings?: PrivacySetting[]
  isActive?: boolean
}

export function useUpdateShareToken(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateShareTokenInput) => {
      const { data: updatedData } = await $axios.patch<ShareToken>(
        `/api/trips/${unref(tripId)}/share-tokens/${data.token}`,
        data,
      )
      return updatedData
    },
    async onSuccess(data) {
      await queryClient.invalidateQueries(shareTokenQueryKeys.list(tripId))
      await queryClient.invalidateQueries(
        shareTokenQueryKeys.detail(tripId, data.token),
      )
    },
  })
}

export function useDeleteShareToken(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (token: string) => {
      await $axios.delete(`/api/trips/${unref(tripId)}/share-tokens/${token}`)
    },
    async onSuccess() {
      await queryClient.invalidateQueries(shareTokenQueryKeys.list(tripId))
    },
  })
}
